import { defineStore } from 'pinia';
import { getSystemInfo, getUrlParams } from '@common/assets/ts/utils';
import type { IState } from './interface';
import { getUserInfo as jsbGetUserInfo, jsbSupportMap, setIosBounces, jsbGetBookStoreNavHeight, setWebviewPullRefresh, jsbBookStoreLoadSuccess, jsbStorage } from '@q-front-npm/jsbridge';
import { apiUserInfo } from '@common/http/api';
export const useStoreCommon = defineStore('storeCommon', {
    state: (): IState => {
        return {
            pageLoading: false,
            urlParams: {},
            systemInfo: {
                appVersion: 0,
                inQmApp: false,
                isIos: false,
                isAndroid: false,
                isHarmony: false,
                isWeixin: false,
                platform: 'android',
                majorVersion: 0,
                isQQ: false,
                isWeibo: false,
            },
            screenRatio: 1, // 屏幕比例
            appMaxWidth: 540, // 页面最大宽
            /** 用户信息 */
            userInfo: {
                avatar: '',
                nickname: '',
                uid: 0,
                user_type: 0, //  0=白板  3=游客  2=老用户  1=新用户
            },
            inTopTab: false, // 是否在顶部tab，书城顶部tab
            inTopTabNavHeight: 0, // 书城顶部tab导航栏高度
            statusBarHeight: 0, // 状态栏高度
            headerHeight: 0, // 导航栏高度
        };
    },
    actions: {
        async initInTopTab() {
            if (!this.inTopTab) {
                setIosBounces(false); // 默认禁止弹性
                return;
            }
            if (import.meta.env.VITE_ENV === 'dev') {
                this.inTopTabNavHeight = 60;
            } else {
                const inTopTabNavHeight = await jsbStorage.get('inTopTabNavHeight', 'activityCommon');
                if (inTopTabNavHeight) {
                    this.inTopTabNavHeight = +inTopTabNavHeight;
                } else {
                    this.inTopTabNavHeight = +(await jsbGetBookStoreNavHeight()) as number;
                    jsbStorage.set('inTopTabNavHeight', this.inTopTabNavHeight, 'activityCommon');
                }
            }
            const styleElement = document.createElement('style');
            styleElement.id = 'in-top-tab-style';
            document.head.appendChild(styleElement);
            styleElement.sheet!.insertRule(`.q-popup-center{ padding-top: ${this.inTopTabNavHeight}px }`, 0);
            // 开启webview下拉刷新
            setWebviewPullRefresh(true);
            jsbBookStoreLoadSuccess(this.urlParams.activity_id);
        },
        /** 获取url参数 */
        async initUrlParams() {
            this.urlParams = getUrlParams();
            console.log('-- urlParams', this.urlParams);
            this.inTopTab = this.urlParams.source === 'book_mall';
        },
        /** 获取系统信息 */
        initSystemInfo() {
            this.systemInfo = getSystemInfo();
            console.log('-- systemInfo', this.systemInfo);
        },
        /** 设置页面loading */
        setPageLoading(data: boolean) {
            this.pageLoading = data;
        },
        /** 动态宽度 - 屏幕适配规则
         * 1. 屏宽小于540 使用屏宽等比缩放，页面内容宽 = 屏宽
         * 2. 屏宽大于540 保持宽高比不变，ratio = 360 / 600
         *    ratio < 0.7 页面内容宽 = 540
         *    ratio >= 0.7 && ratio < 1 使用宽度作为基准，页面内容宽 = 屏宽 * ratio
         *    ratio >= 1，使用高度作为基准，页面内容宽 = 屏高 * ratio
         */
        dynamicWidth() {
            const { clientWidth, clientHeight, } = window.document.documentElement;
            let newWidth = (clientWidth * 360) / 600;
            const defaultMaxWidth = 540;
            if (clientWidth < defaultMaxWidth) {
                newWidth = clientWidth;
            } else {
                const ratio = clientWidth / clientHeight;
                if (ratio < 0.7) {
                    newWidth = defaultMaxWidth;
                } else if (ratio >= 1) {
                    newWidth = (clientHeight * 360) / 600;
                }
            }
            this.appMaxWidth = newWidth;
            this.screenRatio = (newWidth === defaultMaxWidth ? clientWidth : newWidth) / 360;
            // 检查是否已存在动态样式标签
            let styleElement = document.getElementById('dynamic-width-style') as HTMLStyleElement;
            if (!styleElement) {
                styleElement = document.createElement('style');
                styleElement.id = 'dynamic-width-style';
                document.head.appendChild(styleElement);
            }

            // 清空现有样式规则
            while (styleElement.sheet && styleElement.sheet.cssRules.length > 0) {
                styleElement.sheet.deleteRule(0);
            }

            console.log('-- maxWidth', newWidth);
            console.log('-- screenRatio', this.screenRatio);
            styleElement.sheet!.insertRule(`.page-wrapper,.q-popup,.q-popup-content,body{ max-width: ${newWidth}px;margin-left: auto;margin-right: auto;}`, 0);
            const htmlWidth = clientWidth <= 540 ? clientWidth : this.appMaxWidth;
            styleElement.sheet!.insertRule(`html { font-size: ${(htmlWidth / 720) * 100}px !important; }`, 1);
            // 处于书城tab不能禁止弹性，否则下拉刷新会无效
            if (!this.inTopTab) {
                styleElement.sheet!.insertRule(`html{ overscroll-behavior: none }`, 2);
            }
        },
        /**
         * 获取用户信息
         * 1. 优先使用jsb获取
         * 2. 低版本使用接口获取
         */
        async getUserInfo() {
            let res;
            if (jsbSupportMap.user.getUserInfo) {
                res = await jsbGetUserInfo('id,avatar,is_user_login,is_user_tourist_mode,is_whiteboard_account,nickname,wechat_name');
                console.log('-- jsbGetUserInfo', res);
                const { id, is_whiteboard_account, nickname, wechat_name, is_user_tourist_mode, } = res;
                res.uid = id || 0;
                res.nickname = nickname || wechat_name;
                //user_type: 0=白板  3=游客  2=老用户  1=新用户
                res.user_type = is_whiteboard_account ? 0 : is_user_tourist_mode ? 3 : 2;
            } else {
                // 自行替换接口获取用户信息
                res = await apiUserInfo();
            }
            this.userInfo = res;
            return res;
        },
    },
});
